import { io } from "socket.io-client";
import apiClient, { API_ENDPOINTS, getSocketBaseUrl, getAuthToken } from "../config/api";
import { featureFlags } from "../config/featureFlags";

class ScannerService {
  constructor() {
    this.connections = new Map();
  }

  async startScan(targetUrl, scanTypes, options = {}) {
    const payload = {
      target_url: targetUrl,
      scan_types: scanTypes,
      options: {
        enable_ai_analysis: options.enable_ai_analysis !== false,
        enable_mitre_mapping: options.enable_mitre_mapping !== false,
        include_low_risk: options.include_low_risk ?? true,
        deep_scan: options.deep_scan || false,
        scan_mode: options.scan_mode || "standard",
        timeout_minutes: options.timeout_minutes || undefined,
        business_context: options.business_context || null,
        compliance_frameworks: options.compliance_frameworks || null,
        telemetry: options.telemetry || null,
      },
    };

    try {
      const response = await apiClient.post(API_ENDPOINTS.scans.comprehensive.start, payload);
      if (!response.data?.scan_id) {
        throw new Error("No scan ID returned from backend");
      }
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.detail || error.message || "Failed to start scan");
    }
  }

  async getScanResults(scanId) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.scans.comprehensive.result(scanId));
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.detail || "Failed to get scan results");
    }
  }

  async getScanSummary(scanId, options = {}) {
    try {
      const params = {};
      if (options.mode) {
        params.mode = options.mode;
      }
      if (typeof options.maxChars === 'number') {
        params.max_chars = options.maxChars;
      }
      if (options.force_refresh === true) {
        params.force_refresh = true;
      }
      if (options.attempt) {
        params.attempt = options.attempt;
      }

      console.log(`[SUMMARY_REQUEST] Fetching summary for ${scanId}, attempt ${options.attempt || 1}, mode: ${options.mode}`);

      const response = await apiClient.get(API_ENDPOINTS.scans.comprehensive.summary(scanId), {
        params,
        timeout: 120000
      });

      console.log(`[SUMMARY_RESPONSE] Success: ${response.data?.summary?.substring(0, 100) || 'empty'}`);
      return response.data;
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const message = typeof detail === 'object' ? detail?.error : detail;

      console.error(`[SUMMARY_ERROR] Failed to fetch summary. Status: ${status}, Error:`, message || error?.message);

      // Enhance error message with details from backend
      let enhancedError = message || error?.message || "Failed to generate scan summary";
      if (detail?.error_stage) {
        enhancedError += ` (Stage: ${detail.error_stage})`;
      }
      if (detail?.attempts) {
        enhancedError += ` - Attempt ${detail.attempts}/${detail.attempts || 3}`;
      }

      const err = new Error(enhancedError);
      err.response = error?.response;
      err.originalError = error;
      throw err;
    }
  }

  async generateDetailedMissionBrief(scanId, scanResults, promptContext = {}) {
    try {
      const vulnerabilities = Array.isArray(scanResults?.vulnerabilities)
        ? scanResults.vulnerabilities.slice(0, 80)
        : [];

      const payload = {
        scan_id: scanId,
        target_url: scanResults?.target_url || 'Unknown',
        vulnerabilities,
        business_context: {
          description:
            promptContext.description ||
            'Generate an in-depth mission brief for security leadership and engineering. Include attack chain reasoning, business impact, compliance exposure, prioritized remediation timeline, and confidence qualifiers.',
          audience: promptContext.audience || 'security leadership and engineering',
          output_format: promptContext.output_format || 'markdown',
          summary_depth: promptContext.summary_depth || 'detailed'
        }
      };

      const response = await apiClient.post('/api/v1/intelligence/ai/enrich-scan', payload);
      const summary =
        response?.data?.scan?.detailed_summary ||
        response?.data?.scan?.executive_summary ||
        response?.data?.detailed_summary ||
        response?.data?.executive_summary ||
        '';

      if (!summary) {
        throw new Error('Detailed mission brief was not returned by AI service');
      }

      return {
        summary,
        detailed_summary: summary,
        cached: false,
        source: 'ai_enrichment'
      };
    } catch (error) {
      throw new Error(error?.response?.data?.detail || error?.message || 'Failed to generate detailed mission brief');
    }
  }

  async getScanStatus(scanId) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.scans.comprehensive.status(scanId), { suppressToast: true });
      return response.data;
    } catch (error) {
      if (error.name === "APIError") throw error;
      throw new Error(error?.response?.data?.detail || "Failed to get scan status");
    }
  }


  async listScans(skip = 0, limit = 10, status = null) {
    try {
      const params = { skip: Math.max(0, skip || 0), limit: Math.max(1, limit || 10) };
      if (status) {
        params.status = status;
      }
      const response = await apiClient.get(API_ENDPOINTS.scans.comprehensive.list, { params });
      const rawScans = Array.isArray(response.data?.scans)
        ? response.data.scans
        : Array.isArray(response.data)
          ? response.data
          : [];
      const scans = rawScans.map((scan) => ({
        scan_id: scan.scan_id || scan.id,
        target_url: scan.target_url,
        status: scan.status,
        started_at: scan.started_at,
        ...scan,
      }));
      return {
        scans,
        total: typeof response.data?.total === "number" ? response.data.total : scans.length,
        skip: params.skip,
        limit: params.limit,
      };
    } catch (error) {
      throw new Error(error?.response?.data?.detail || "Failed to list scans");
    }
  }

  async cancelScan(scanId) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.scans.comprehensive.cancel(scanId));
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.detail || "Failed to cancel scan");
    }
  }

  async deleteScan(scanId) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.scans.comprehensive.delete(scanId));
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.detail || "Failed to delete scan");
    }
  }

  async deleteMultipleScans(scanIds) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.scans.comprehensive.deleteBulk, { data: scanIds });
      return response.data;
    } catch (error) {
      throw new Error(error?.response?.data?.detail || "Failed to delete scans");
    }
  }

  setupWebSocket(scanId, callbacks = {}) {
    const pollIntervalMs = 2000;
    let closed = false;
    let socket = null;
    let lastStatus = null;
    let pollTimer = null;

    const connection = {
      close: () => {
        if (closed) {
          return;
        }
        closed = true;
        if (socket) {
          socket.disconnect();
          socket = null;
        }
        if (pollTimer) {
          clearInterval(pollTimer);
        }
        this.connections.delete(scanId);
        callbacks.onClose?.();
      },
      readyState: 1,
    };

    const poll = async () => {
      if (closed) {
        return;
      }
      try {
        const status = await this.getScanStatus(scanId);
        lastStatus = status;
        callbacks.onProgress?.(status);

        const status_lower = (status.status || "").toLowerCase();
        if (status_lower === "completed") {
          const result = await this.getScanResults(scanId);
          callbacks.onComplete?.(result);
          connection.close();
        } else if (["failed", "cancelled"].includes(status_lower)) {
          callbacks.onError?.(new Error(status.status_message || `Scan ${status.status}`));
          connection.close();
        }
      } catch (error) {
        if (error?.name === "APIError" && error?.statusCode === 404) {
          console.debug("Scan not yet available in database, waiting for next poll...");
          return;
        }
        callbacks.onError?.(error);
      }
    };

    const startPolling = () => {
      if (closed || pollTimer) {
        return;
      }
      poll();
      pollTimer = setInterval(poll, pollIntervalMs);
      const activeConnection = this.connections.get(scanId);
      if (activeConnection) {
        activeConnection.timer = pollTimer;
      }
    };

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const handleSocketEvent = (payload = {}) => {
      const data = payload.data || payload;
      lastStatus = {
        scan_id: payload.scan_id || data.scan_id || scanId,
        status: data.scan_status || data.status || payload.event_type || "running",
        progress: data.progress_percent ?? data.progress ?? payload.progress_percent ?? payload.progress ?? 0,
        current_stage: data.current_stage || data.phase,
        status_message: data.status_message || data.message,
        summary: data.summary,
      };
      callbacks.onProgress?.(lastStatus);

      const status_lower = String(lastStatus.status || "").toLowerCase();
      if (status_lower === "completed") {
        this.getScanResults(scanId)
          .then((result) => callbacks.onComplete?.(result))
          .catch((error) => callbacks.onError?.(error))
          .finally(() => connection.close());
      } else if (["failed", "cancelled"].includes(status_lower)) {
        callbacks.onError?.(new Error(lastStatus.status_message || `Scan ${lastStatus.status}`));
        connection.close();
      }
    };

    try {
      const token = getAuthToken();
      if (!featureFlags.realtimeScanUpdates) {
        startPolling();
        this.connections.set(scanId, { socket, timer: pollTimer, lastStatus });
        callbacks.onOpen?.();
        return connection;
      }

      const socketBaseUrl = getSocketBaseUrl(featureFlags.useBffSocket);
      socket = io(socketBaseUrl, {
        auth: token ? { token } : {},
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 8000,
      });

      socket.on("connect", () => {
        callbacks.onOpen?.();
        socket.emit("join_scan", { scan_id: scanId });
        stopPolling(); // Stop the redundant fallback polling once connected
      });
      socket.on("connect_error", startPolling);
      socket.on("disconnect", () => {
        if (!closed) {
          startPolling();
        }
      });
      ["scan:started", "scan:progress", "scan:analyzing", "scan:vulnerability_found", "scan:completed", "scan:failed", "scan:cancelled"].forEach((eventName) => {
        socket.on(eventName, handleSocketEvent);
      });
    } catch (error) {
      callbacks.onError?.(error);
      startPolling();
    }

    if (!socket) {
      startPolling();
    }
    this.connections.set(scanId, { socket, timer: pollTimer, lastStatus });
    return connection;
  }

  closeWebSocket(scanId) {
    const connection = this.connections.get(scanId);
    if (!connection) {
      return;
    }
    if (connection.socket) {
      connection.socket.disconnect();
    }
    if (connection.timer) {
      clearInterval(connection.timer);
    }
    this.connections.delete(scanId);
  }

  closeAllWebSockets() {
    for (const [scanId, connection] of this.connections.entries()) {
      if (connection.socket) {
        connection.socket.disconnect();
      }
      if (connection.timer) {
        clearInterval(connection.timer);
      }
      this.connections.delete(scanId);
    }
  }

  isWebSocketActive(scanId) {
    return this.connections.has(scanId);
  }

  getActiveScanCount() {
    return this.connections.size;
  }
}

const scannerService = new ScannerService();
export default scannerService;
